import { shopifyCopy } from '@discovr/features/copy';
import React, { useState } from 'react';
// KPI + sub-tab icons: the exact Phosphor glyphs Figma uses (nodes 1672:33333
// and 1672:33433). Play is aliased to avoid the lucide Play collision.
import { CurrencyInr, Receipt, Stack, Play as PlayDuotone, CursorClick, Lightning, ChartBar, Gauge, Pulse, ChartLineUp, FilmSlate, TrendUp, Eye, type Icon } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { KpiCard } from '../components/common/KpiCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

import { SentimentAnalysisTab } from './SentimentAnalysisTab';
import { CampaignCreatorContentTab } from './CampaignCreatorContentTab';
import { AudienceDynamicsTab } from './AudienceDynamicsTab';
import { ConversionsTab } from './ConversionsTab';
import { ShopifyConversionsTab } from './ShopifyConversionsTab';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { useNavigate } from '@tanstack/react-router';
import { useGeneralReport, useCampaignCapabilitiesStore } from '@discovr/features';
import type { GeneralReport, ReportStatus } from '@discovr/types';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../components/ui/chart';
import type { ChartConfig } from '../components/ui/chart';
import { axisTicks, rollingWindowStart, formatAxisTick, TIME_PERIOD_OPTIONS } from './reportingWindow';
import type { AxisTickLabel, TimePeriod } from './reportingWindow';
import { activityEntriesFromReport, chartPointsFromReport, kpiStatsFromReport, unavailableKpiStats, type KpiStat } from './reportFormat';
import { REPORT_SWITCHING_CLASS, isReportLoadFailed, isReportRefreshFailed } from './reportLoadState';
import { ReportRefreshNotice } from './ReportRefreshNotice';
import { RefreshCw } from '../icons';
import { useCampaignReportingDeepLink } from '../campaign-detail/useCampaignReportingDeepLink';
import { useCampaignReportingWindow } from '../campaign-detail/useCampaignReportingWindow';
import { usePublishShareWindow } from '../campaign-detail/hooks/usePublishShareWindow';
import { TruncatedText } from '../components/common/TruncatedText';
import { ReportExportButton } from './ReportExportButton';

const overviewChartConfig: ChartConfig = {
  // Figma "Total Views Chart" line = brand violet (#5A07CE = --brand-600).
  reach: { label: 'Total Views', color: 'var(--brand-600)' },
};

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`rounded-xl border border-border bg-card overflow-hidden ${className ?? ''}`}>
    {children}
  </div>
);

// Two-line x-axis tick: day number always, month name only on the leftmost or
// a month-boundary tick (see formatAxisTick). A custom `tick` element (not
// `tickFormatter`, which is single-line) is needed for the second text line.
const TwoLineDateTick: React.FC<{
  x?: number;
  y?: number;
  payload?: { value: number };
  formatter: (ts: number) => AxisTickLabel;
}> = ({ x, y, payload, formatter }) => {
  if (x == null || y == null || !payload) return null;
  const { primary, secondary } = formatter(payload.value);
  const emphasized = secondary != null;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        y={12}
        textAnchor="middle"
        fontSize={12}
        fontWeight={emphasized ? 600 : 400}
        fill={emphasized ? 'var(--foreground)' : 'var(--muted-foreground)'}
      >
        {primary}
      </text>
      {secondary && (
        <text y={27} textAnchor="middle" fontSize={10.5} fontWeight={600} fill="var(--foreground)" letterSpacing="0.02em">
          {secondary}
        </text>
      )}
    </g>
  );
};

// Exact Phosphor glyphs Figma uses for the GI KPI cards (node 1672:33333),
// rendered duotone. CPV isn't in the Figma — Lightning is the closest match.
const KPI_META: Record<string, Icon> = {
  'Campaign Budget': CurrencyInr,
  'Live Content':    Stack,
  'Views':           PlayDuotone,
  'Engagement':      CursorClick,
  'CPV':             Lightning,
};

const StatCard = ({ label, value, tooltip, hint, hintDetail }: KpiStat) => (
  <KpiCard icon={KPI_META[label] ?? ChartBar} label={label} value={value} valueTooltip={tooltip} hint={hint} hintDetail={hintDetail} />
);

const DAY_MS = 86_400_000;
// Local 'full' start when firstLiveAt is unknown too: the trailing week.
const LOCAL_FULL_FALLBACK_MS = 7 * DAY_MS;

// A views window the backend actually resolved. An 'unavailable' block carries
// Go's zero time (0001-01-01), which would stretch the axis across millennia.
const RESOLVED_WINDOW_STATUSES: ReadonlySet<ReportStatus> = new Set<ReportStatus>(['ok', 'no_data']);

/** The chart's x-domain: the backend's window when it resolved one, else a
 *  local now-based window so the axis still reads while loading or on failure. */
function chartWindow(
  report: GeneralReport | undefined,
  timePeriod: TimePeriod,
  firstLiveAt: string | undefined,
  now: number,
): { start: number; end: number } {
  if (report && RESOLVED_WINDOW_STATUSES.has(report.views.status)) {
    return { start: Date.parse(report.views.window.start), end: Date.parse(report.views.window.end) };
  }
  return { start: rollingWindowStart(timePeriod, now, firstLiveAt, now - LOCAL_FULL_FALLBACK_MS), end: now };
}

const REPORT_LOAD_FAILED_TEXT = "Couldn't load this right now.";
const NO_ACTIVITY_TEXT = 'No activity yet';
const ACTIVITY_UNAVAILABLE_TEXT = "Activity isn't available right now.";

function LoadFailedNotice({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <p className="m-0 text-body-sm-regular text-muted-foreground">{REPORT_LOAD_FAILED_TEXT}</p>
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw size={12} className="size-3" /> Retry
      </Button>
    </div>
  );
}

/** The activity card's one-line notice, or null when rows (or a pending
 *  first load) fill the card. */
function activityNotice({
  loading,
  status,
  count,
}: {
  loading: boolean;
  status: ReportStatus | undefined;
  count: number;
}): string | null {
  if (loading) return null;
  if (status === 'unavailable') return ACTIVITY_UNAVAILABLE_TEXT;
  return count === 0 ? NO_ACTIVITY_TEXT : null;
}

interface CampaignOverviewTabProps {
  campaignData: Record<string, unknown> | undefined;
  handleOpenEditFollowerRanges: () => void;
  liveAnalytics?: Record<string, unknown>;
  /** ISO timestamp of the first video that went live in this campaign. */
  firstLiveAt?: string;
  /** @deprecated No-op — live analytics are built from the roster cache at render time. */
  fetchLiveAnalytics?: (items: unknown[], opts?: { force?: boolean }) => Promise<void>;
  campaignId?: string;
  /** Admin app only: shows "Export to Excel" on General Insights. The brand
   *  app leaves it off; the server also refuses non-admin callers. */
  showReportExport?: boolean;
}

// The unified 4-option scheme, shared with SentimentAnalysisTab so the two
// windowed reporting dropdowns can't drift apart.
const TIME_PERIODS = TIME_PERIOD_OPTIONS;

const SUB_TABS: Array<{ id: 'overview' | 'sentiment' | 'audienceDynamics' | 'creatorContent' | 'conversions'; label: string; icon: Icon }> = [
  // Tab icons: the exact Phosphor glyphs Figma uses (node 1672:33433).
  { id: 'overview',        label: 'General Insights',       icon: Gauge       },
  { id: 'sentiment',       label: 'Sentiment Intelligence', icon: Pulse       },
  { id: 'audienceDynamics',label: 'Audience Dynamics',      icon: ChartLineUp },
  { id: 'creatorContent',  label: 'Creator Content',        icon: FilmSlate   },
  { id: 'conversions',     label: 'Conversions',            icon: Receipt },
];

// Per-sub-tab head copy (Figma 1672:33328) — title + subtitle shown above each
// sub-tab body, with a shared "Last 7 days" period dropdown on the right.
const SUB_TAB_HEADERS: Record<string, { title: string; subtitle: string }> = {
  overview:         { title: 'General Insights',       subtitle: 'Reach, spend and engagement at a glance.' },
  sentiment:        { title: 'Sentiment Intelligence', subtitle: 'How audiences are reacting now.' },
  audienceDynamics: { title: 'Audience Dynamics',      subtitle: 'Who your views actually reached.' },
  creatorContent:   { title: 'Creator Content',        subtitle: "Every creator's live content performance." },
  conversions:      { title: 'Conversions',            subtitle: 'Sales from your connected tools.' },
};

export const CampaignOverviewTab: React.FC<CampaignOverviewTabProps> = ({
  campaignData = {},
  liveAnalytics: _liveAnalytics = {},
  firstLiveAt,
  fetchLiveAnalytics: _fetchLiveAnalytics,
  campaignId,
  showReportExport = false,
}) => {
  // ── Reporting sub-tab — URL-driven (?tab=reporting&sub=<sub-tab>) ────────────
  // URL is the source of truth: setSubTab writes ?sub, activeSubTab is derived,
  // so a refresh or shared link reopens the exact view (default overview).
  const { sub: deepLinkSubTab, setSub: setSubTab } = useCampaignReportingDeepLink();
  const activeSubTab = deepLinkSubTab ?? 'overview';
  // A deep link can select a tab the overflow-x strip hides at scrollLeft 0.
  const subTabBarRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const bar = subTabBarRef.current;
    const tab = bar?.querySelector('[aria-current="page"]');
    if (!bar || !tab) return;
    // scrollLeft, not scrollIntoView: that can also scroll the page vertically.
    const barRect = bar.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    if (tabRect.left < barRect.left) bar.scrollLeft -= barRect.left - tabRect.left;
    else if (tabRect.right > barRect.right) bar.scrollLeft += tabRect.right - barRect.right;
  }, [activeSubTab]);
  const showExportButton = showReportExport && activeSubTab === 'overview';

  // Window selection is URL-owned (?win=), shared with Sentiment Intelligence.
  const { window: timePeriod, setWindow: setTimePeriod } = useCampaignReportingWindow();
  const generalReport = useGeneralReport(campaignId ?? '', timePeriod);
  const report = generalReport.data;
  const isLoading = generalReport.isLoading;
  const loadFailed = isReportLoadFailed(generalReport);
  const refreshFailed = isReportRefreshFailed(generalReport);
  const isSwitchingPeriod = generalReport.isPlaceholderData;
  const retryReport = () => void generalReport.refetch({ cancelRefetch: false });
  // The backend answered, but couldn't build that block: same treatment as a failed load.
  const kpisUnavailable = report?.kpis.status === 'unavailable';
  const chartLoadFailed = loadFailed || report?.views.status === 'unavailable';
  // No report and not loading (failed, or no campaign id yet), or KPIs the
  // backend couldn't build: five "—" tiles.
  const stats = React.useMemo(
    () =>
      report && !kpisUnavailable
        ? kpiStatsFromReport(report.kpis, report.currency)
        : unavailableKpiStats(),
    [report, kpisUnavailable],
  );
  // Publish the resolved window for the admin-header share button, only while
  // General Insights is active. `null` is a no-op (not a clear) so this doesn't
  // race SentimentAnalysisTab's own call to the same hook.
  usePublishShareWindow(activeSubTab === 'overview' ? timePeriod : null);

  // Conversions sub-tab: each attribution source reports its own relevance
  // (undefined = loading). The shared "connect a tool" empty state shows only
  // once both have answered and neither is relevant.
  const [appsflyerRelevant, setAppsflyerRelevant] = useState<boolean | undefined>(undefined);
  const [shopifyRelevant, setShopifyRelevant] = useState<boolean | undefined>(undefined);
  const navigate = useNavigate();
  const lane = useCampaignCapabilitiesStore((s) => s.lane);
  const noAttributionSourceConnected =
    appsflyerRelevant === false && shopifyRelevant === false;

  const shopifyEnabled =
    campaignData && (campaignData.id ?? campaignData._id)
      ? Boolean(
          (campaignData.integrations as { shopify?: { enabled?: boolean } } | undefined)?.shopify
            ?.enabled,
        )
      : undefined;

  const isSpecialBrand = React.useMemo(() => {
    const cId = String(campaignData?.id ?? campaignData?._id ?? '').toLowerCase();
    const cName = String(campaignData?.name ?? '').toLowerCase();
    const url = typeof window !== 'undefined' ? window.location.href.toLowerCase() : '';

    return (
      cId.includes('cetaphil') || cName.includes('cetaphil') || url.includes('cetaphil') ||
      cId.includes('naagin') || cName.includes('naagin') || url.includes('naagin')
    );
  }, [campaignData]);

  // The backend already windowed, trimmed and anchored these points.
  const filteredGrowthData = React.useMemo(
    () => chartPointsFromReport(report?.views.points ?? null),
    [report],
  );

  // "Views Over Time" x-range: the backend's window, already started at the
  // first point so the line begins at the left edge; ticks follow its span.
  const { windowStart, windowEnd, periodTicks } = React.useMemo(() => {
    // eslint-disable-next-line react-hooks/purity -- the local fallback window ends at wall-clock now while the report is loading or unavailable.
    const { start, end } = chartWindow(report, timePeriod, firstLiveAt, Date.now());
    return { windowStart: start, windowEnd: end, periodTicks: axisTicks(start, end) };
  }, [timePeriod, firstLiveAt, report]);

  const tickFormatter = React.useCallback(
    (ts: number) => formatAxisTick(ts, { windowStart, spanMs: windowEnd - windowStart }),
    [windowStart, windowEnd],
  );

  const recentActivity = React.useMemo(
    () => activityEntriesFromReport(report?.activity.items ?? null),
    [report],
  );

  const renderOverviewContent = () => {
    const KPI_CARD_COUNT = 5;
    const activityNote = activityNotice({
      loading: isLoading || loadFailed,
      status: report?.activity.status,
      count: recentActivity.length,
    });
    return (
    <div className={`flex flex-col gap-5 w-full ${isSwitchingPeriod ? REPORT_SWITCHING_CLASS : ''}`} aria-busy={isSwitchingPeriod}>
      {refreshFailed && <ReportRefreshNotice onRetry={retryReport} />}
      {/* KPI Row */}
      <div className="grid w-full grid-cols-2 gap-5 lg:grid-cols-5">
        {isLoading ? (
          <>
            {Array.from({ length: KPI_CARD_COUNT }, (_, i) => (
              <div key={i} className="flex h-[84px] items-center gap-3 rounded-xl border border-border bg-card px-4 py-[18px]">
                <div className="size-12 shrink-0 rounded-full bg-muted" />
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="h-3 w-1/2 animate-pulse rounded-sm bg-muted" />
                  <div className="h-7 w-2/5 animate-pulse rounded-md bg-muted" />
                </div>
              </div>
            ))}
          </>
        ) : (
          stats.map((kpi, i) => (
            <StatCard key={i} {...kpi} />
          ))
        )}
      </div>

      {/* Performance Pulse */}
      <div className="flex flex-col gap-3">
        <Card className="px-6 py-5">
          {/* Head: title + subtitle on the left; legend dot + period dropdown
              (DS Select, four windows) on the right. */}
          <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
            {/* Figma 1672:33341 — two text lines only, no headline figure. */}
            <div className="flex flex-col gap-1">
              <h3 className="text-[16px] font-semibold leading-none text-foreground">Campaign views</h3>
              <p className="text-body-sm-regular text-muted-foreground">
                Daily reach for this campaign
              </p>
            </div>
            <div className="flex flex-col items-end gap-3">
              <div className="flex items-center gap-1.5">
                <span className="size-2 rounded-full" style={{ backgroundColor: 'var(--brand-600)' }} />
                <span className="text-[12px] font-medium text-muted-foreground">Total Views</span>
              </div>
              <Select
                value={timePeriod}
                onValueChange={(v) => setTimePeriod(v as TimePeriod)}
              >
                <SelectTrigger
                  size="sm"
                  className="h-8 gap-1.5 rounded-lg px-3 text-[12px] font-medium text-foreground shadow-none"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  {TIME_PERIODS.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="text-[12px]">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {isLoading ? (
            <div className="flex h-[260px] items-center justify-center">
              <svg className="size-7 animate-spin text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
            </div>
          ) : chartLoadFailed ? (
            <div className="flex h-[260px] items-center justify-center">
              <LoadFailedNotice onRetry={retryReport} />
            </div>
          ) : report?.views.status === 'no_data' || filteredGrowthData.length === 0 ? (
            <div className="relative h-[260px]">
              <ChartContainer config={overviewChartConfig} className="size-full">
                {/* A truly empty array makes recharts render zero Y-axis ticks
                    regardless of explicit ticks/domain — one placeholder record
                    per tick gives it something to lay out against. */}
                <AreaChart data={periodTicks.map((x) => ({ x }))}>
                  <CartesianGrid stroke="var(--color-stone-200)" vertical={false} />
                  <XAxis
                    dataKey="x"
                    type="number"
                    domain={[windowStart, windowEnd]}
                    ticks={periodTicks}
                    // Force every tick to render — recharts' default interval
                    // auto-thins by width and would drop the leftmost/month-
                    // boundary ticks the two-line formatter depends on.
                    interval={0}
                    height={40}
                    tick={<TwoLineDateTick formatter={tickFormatter} />}
                    tickLine={false}
                    axisLine={false}
                  />
                  {/* Y-axis tick labels don't render here even with real data;
                      width reserves the gutter and tick={false} defers to the
                      manually-positioned column below. */}
                  <YAxis width={44} axisLine={false} tickLine={false} tick={false} ticks={[0, 5000, 10000, 15000]} domain={[0, 15000]} />
                </AreaChart>
              </ChartContainer>
              <div className="pointer-events-none absolute inset-y-1 left-1 bottom-6 flex w-10 flex-col justify-between text-right">
                {[15000, 10000, 5000, 0].map((v) => (
                  <span key={v} className="text-[12px] leading-none text-muted-foreground">
                    {v === 0 ? '0' : `${(v / 1000).toFixed(0)}K`}
                  </span>
                ))}
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
                <div className="flex size-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Eye size={28} />
                </div>
                <div className="space-y-1 text-center">
                  <p className="text-body-md-semibold text-foreground m-0">
                    Views start streaming at launch
                  </p>
                  <p className="max-w-[280px] text-body-sm-regular text-muted-foreground m-0">
                    Once your first content goes live, this chart builds within hours.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-[260px]">
              <ChartContainer config={overviewChartConfig} className="size-full">
                <AreaChart data={filteredGrowthData}>
                  <CartesianGrid stroke="var(--color-stone-200)" vertical={false} />
                  <XAxis
                    dataKey="x"
                    type="number"
                    domain={[windowStart, windowEnd]}
                    ticks={periodTicks}
                    interval={0}
                    height={40}
                    tick={<TwoLineDateTick formatter={tickFormatter} />}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
                    tickFormatter={(v) =>
                      v >= 1_000_000_000
                        ? `${(v / 1_000_000_000).toFixed(1)}B`
                        : v >= 1_000_000
                          ? `${(v / 1_000_000).toFixed(1)}M`
                          : v >= 1_000
                            ? `${(v / 1_000).toFixed(0)}K`
                            : String(v)
                    }
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        labelFormatter={(ts) => {
                          const d = new Date(Number(ts));
                          return Number.isNaN(d.getTime())
                            ? ''
                            : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        }}
                        formatter={(value) => {
                          const n = Number(value);
                          return n >= 1_000_000_000
                            ? `${(n / 1_000_000_000).toFixed(2)}B`
                            : n >= 1_000_000
                              ? `${(n / 1_000_000).toFixed(2)}M`
                              : n >= 1_000
                                ? `${(n / 1_000).toFixed(1)}K`
                                : String(n);
                        }}
                      />
                    }
                  />
                  <Area
                    isAnimationActive={false}
                    type="monotone"
                    dataKey="reach"
                    stroke="var(--color-reach)"
                    strokeWidth={2}
                    fill="none"
                  />
                </AreaChart>
              </ChartContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Recent campaign activity — Figma "Recent Activity" (1672:33305):
          18px medium title, then rows of {24px avatar, single-line sentence,
          relative timestamp}. No leading icon, no event sub-line. */}
      <Card className="p-5">
        <h3 className="mb-5 text-body-lg-regular/snug font-medium text-foreground">
          Recent campaign activity
        </h3>
        {loadFailed ? (
          <LoadFailedNotice onRetry={retryReport} />
        ) : activityNote ? (
          <p className="m-0 text-body-sm-regular text-muted-foreground">{activityNote}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {recentActivity.slice(0, 5).map((activity) => {
              // Compose the line as Figma shows it: "{actor} {verb phrase}" in a
              // single uniform weight, from the backend's sentence parts.
              const phrase = activity.parts.length
                ? activity.parts.map((p) => p.text).join('')
                : activity.sentence;
              const line = `${activity.name} ${phrase}`.trim();
              const when = activity.event;
              return (
                <div
                  key={activity.id}
                  // Figma "ev" row: bg #FCFCFB, border #F5F5F5, 8px radius, pad 10/12.
                  className="flex items-center gap-3 rounded-lg border border-[#F5F5F5] bg-[#FCFCFB] px-3 py-2.5"
                >
                  {/* Avatar — profile pic if available, neutral initial otherwise.
                      onError reveals the initial layer. */}
                  <div className="relative flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-[11px] font-medium text-muted-foreground">
                    <span className="select-none leading-none">
                      {((activity.name || '?')[0] ?? '?').toUpperCase()}
                    </span>
                    {activity.avatarUrl && (
                      <img
                        src={activity.avatarUrl}
                        alt=""
                        className="absolute inset-0 size-full object-cover"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <TruncatedText as="p" text={line} className="text-[12px] font-medium text-foreground" />
                    {activity.detail && (
                      <div data-activity-detail>
                        <TruncatedText as="p" text={activity.detail} className="mt-0.5 text-[11px] text-muted-foreground" />
                      </div>
                    )}
                    {when && <p className="mt-0.5 text-[10px] text-(--icon-subtle)">{when}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
    );
  };

  return (
    <div className="flex flex-col gap-5 w-full">
      {/* Sub-Navigation — grey baseline rule under the whole strip (matches the
          review-drawer tabs), with the active item's 2px black underline
          overlapping it via -mb-px. */}
      <div className="flex justify-between items-end mb-1 border-b border-border">
        <div
          ref={subTabBarRef}
          className="flex min-w-0 flex-1 gap-6 overflow-x-auto overflow-y-hidden scrollbar-none [&::-webkit-scrollbar]:hidden"
        >
          {SUB_TABS.filter(tab => {
            if (isSpecialBrand && tab.id === 'creatorContent') return false;
            return true;
          }).map((tab) => (
            <Button
              key={tab.id}
              variant="ghost"
              onClick={() => setSubTab(tab.id)}
              aria-current={activeSubTab === tab.id ? 'page' : undefined}
              className={[
                // Tab underline: 2px black bar as an absolute span, not border-b
                // (the global *{border-color} would grey a plain border out).
                // has-[>svg]:px-0.5 keeps the five icon tabs tight enough to fit;
                // shrink-0 + overflow scroll safety so Conversions isn't clipped.
                'relative flex h-auto shrink-0 items-center gap-2 rounded-none px-0.5 has-[>svg]:px-0.5 pb-1.5 pt-1 text-[16px] font-medium leading-6 hover:bg-transparent',
                activeSubTab === tab.id
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {/* size-5 class is required: the Button base shrinks any svg
                  without a size-* class to size-4. */}
              <tab.icon size={20} className="size-5" /> {tab.label}
              {activeSubTab === tab.id && (
                <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-[2px] bg-black" />
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Sub-tab head — title + subtitle on the left, a shared "Last 7 days"
          period dropdown on the right (Figma 1672:33328). The dropdown is a
          non-functional shell for now (no BE date-range param yet). */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-[22px]/[29px] font-medium text-foreground">{SUB_TAB_HEADERS[activeSubTab]?.title}</h2>
          <p className="text-[13px] font-normal text-muted-foreground">{SUB_TAB_HEADERS[activeSubTab]?.subtitle}</p>
        </div>
        {showExportButton && campaignId && <ReportExportButton campaignId={campaignId} />}
        {/* Last 7 days period dropdown — commented out until BE date-range param is wired.
        <button
          type="button"
          title="Coming soon"
          className="flex h-10 shrink-0 items-center gap-2 rounded-full border border-[var(--icon-subtle)] bg-card px-4 text-body-sm-medium text-foreground transition-colors hover:bg-muted"
        >
          <CalendarBlank size={16} className="text-foreground" /> Last 7 days <CaretDown size={14} className="text-foreground" />
        </button>
        */}
      </div>

      {activeSubTab === 'overview' && renderOverviewContent()}
      {activeSubTab === 'sentiment' && (
        <SentimentAnalysisTab campaignId={campaignId} />
      )}
      {activeSubTab === 'audienceDynamics' && (
        <AudienceDynamicsTab
          campaignId={campaignId}
        />
      )}
      {activeSubTab === 'creatorContent' && (
        <CampaignCreatorContentTab
          campaignId={campaignId}
          campaignPlatform={(campaignData as { platform?: string } | undefined)?.platform}
        />
      )}
      {activeSubTab === 'conversions' && (
        // AppsFlyer (installs) + Shopify (sales) attribution, stacked. Each
        // reports its relevance via onConnectionChange rather than its own
        // "connect" card; the shared placeholder shows only when neither is relevant.
        <div className="flex flex-col gap-4">
          <ConversionsTab campaignId={campaignId} onConnectionChange={setAppsflyerRelevant} />
          <ShopifyConversionsTab
            campaignId={campaignId}
            onConnectionChange={setShopifyRelevant}
            shopifyEnabled={shopifyEnabled}
          />
          {noAttributionSourceConnected && (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border py-16 text-center">
              <TrendUp className="size-6 text-indigo-400" />
              <h3 className="text-body-md-semibold text-foreground">
                See the real impact of your campaign
              </h3>
              <p className="max-w-sm text-body-sm-regular text-muted-foreground">
                {lane === 'brand'
                  ? 'Connect Shopify to track sales and referral visits driven by each creator.'
                  : shopifyCopy.state.not_connected.staffBody}
              </p>
              {lane === 'brand' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void navigate({ to: '/integrations' } as never)}
                >
                  Go to Integrations
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
